/* lineOffset implementation from:
https://github.com/Turfjs/turf/issues/1838#issuecomment-927835876
with some changes:
- check whether there is an intersection before accessing it
- iterate the multiplier for transformScale until the intersection is found (better accuracy)
- filter co-linear coordinates from the original set of points
AB, crossproduct, and isParallel are from the original turfjs lineOffset implementation.
*/

import * as turf from '@turf/turf';

/**
 * AB
 *
 * @private
 * @param {Array<Array<number>>} segment - 2 vertex line segment
 * @returns {Array<number>} coordinates [x, y]
 */
function ab(segment) {
  const start = segment[0];
  const end = segment[1];
  return [end[0] - start[0], end[1] - start[1]];
}

/**
 * Cross Product
 *
 * @private
 * @param {Array<number>} v1 coordinates [x, y]
 * @param {Array<number>} v2 coordinates [x, y]
 * @returns {Array<number>} Cross Product
 */
function crossProduct(v1, v2) {
  return v1[0] * v2[1] - v2[0] * v1[1];
}

/**
 * Is Parallel
 *
 * @private
 * @param {Array<number>} a coordinates [x, y]
 * @param {Array<number>} b coordinates [x, y]
 * @returns {boolean} true if a and b are parallel (or co-linear)
 */
function isParallel(a, b) {
  const r = ab(a);
  const s = ab(b);
  return crossProduct(r, s) === 0;
}

export default (line, signedDistance, { units = 'kilometers' } = {}) => {
  const originalLineCoords = line.geometry.coordinates;
  const transformAngle = signedDistance < 0 ? -90 : 90;
  const distance = Math.abs(signedDistance);

  // Filter co-linear coordinates
  const lineCoords = originalLineCoords.filter((coord, index) => {
    if (index === 0) return true;
    if (index + 1 >= originalLineCoords.length) return true;
    return !isParallel(
      [originalLineCoords[index - 1], coord],
      [coord, originalLineCoords[index + 1]],
    );
  });

  const offsetLines = [];
  for (let i = 0; i < lineCoords.length - 1; i += 1) {
    // Translating each segment of the line to correct position
    const angle =
      turf.bearing(lineCoords[i], lineCoords[i + 1]) + transformAngle;
    const firstPoint = turf.transformTranslate(
      turf.point(lineCoords[i]),
      distance,
      angle,
      { units },
    )?.geometry.coordinates;
    const secondPoint = turf.transformTranslate(
      turf.point(lineCoords[i + 1]),
      distance,
      angle,
      { units },
    )?.geometry.coordinates;
    offsetLines.push([firstPoint, secondPoint]);
  }

  const offsetCoords = [offsetLines[0][0]]; // First point inserted
  for (let i = 0; i < offsetLines.length; i += 1) {
    // For each translated segment of the initial line
    if (offsetLines[i + 1]) {
      let intersectionFound = false;
      let multiplier = 1;
      while (!intersectionFound && multiplier < 200) {
        // If there's another segment after this one
        const firstLine = turf.transformScale(
          turf.lineString(offsetLines[i]),
          multiplier,
        ); // transformScale is useful in case the two segment don't have an intersection point
        const secondLine = turf.transformScale(
          turf.lineString(offsetLines[i + 1]),
          multiplier,
        ); // Which happen when the resulting offset line is bigger than the initial one
        // We're calculating the intersection point between the two translated & scaled segments
        const intersection = turf.lineIntersect(firstLine, secondLine);
        if (intersection.features?.length > 0) {
          intersectionFound = true;
          offsetCoords.push(intersection.features[0].geometry.coordinates);
        } else {
          multiplier *= 2;
        }
      }
    } else offsetCoords.push(offsetLines[i][1]); // If there's no other segment after this one, we simply push the last point of the line
  }

  return turf.lineString(offsetCoords);
};
